import type {
  CardDefinitionId,
  EngineGameplayCatalogV1,
  EngineLandCardDefinitionV1,
} from "@fate-kingdom/card-data";

/** Frozen setup input for replay/hash compatibility with checkpoint f05129c. */
export const LEGACY_TEST_CARD_DATA_VERSION_V1 = "0.2.0-test.1" as const;
export const LEGACY_TEST_GAMEPLAY_CATALOG_HASH_V1 =
  "gameplay-catalog-fnv1a64-v1:56785580c4bc52f8" as const;

function legacyCardId(id: string): CardDefinitionId {
  return id as CardDefinitionId;
}

function legacyArmy(index: number) {
  const suffix = String(index).padStart(2, "0");
  return {
    id: legacyCardId(`card.test-filler-army-${suffix}`),
    name: `TEST ONLY Filler Army ${suffix}`,
    type: "army" as const,
  };
}

function legacyLand(
  id: string,
  name: string,
  controls: readonly [number, number, number],
): EngineLandCardDefinitionV1 {
  const cardId = legacyCardId(id);
  return {
    id: cardId,
    name,
    type: "land",
    developmentId: cardId,
    stages: [
      { stage: 1, maximumControl: controls[0] },
      { stage: 2, maximumControl: controls[1] },
      { stage: 3, maximumControl: controls[2] },
    ],
  };
}

export const LEGACY_TEST_ENGINE_CATALOG_V1: EngineGameplayCatalogV1 = {
  schemaVersion: 1,
  cardDataVersion: LEGACY_TEST_CARD_DATA_VERSION_V1,
  gameplayCatalogHash: LEGACY_TEST_GAMEPLAY_CATALOG_HASH_V1,
  cards: [
    ...Array.from({ length: 14 }, (_, index) => legacyArmy(index + 1)),
    legacyLand("card.test-mountain", "TEST ONLY Mountain", [9, 13, 17]),
    legacyLand("card.test-plains", "TEST ONLY Plains", [10, 14, 18]),
    legacyLand("card.test-river", "TEST ONLY River", [8, 12, 16]),
  ],
};

interface LegacyDeckEntry {
  readonly cardId: string;
  readonly count: number;
}

function legacyFillerEntries(first: number, last: number, count: number): LegacyDeckEntry[] {
  return Array.from({ length: last - first + 1 }, (_, offset) => ({
    cardId: `card.test-filler-army-${String(first + offset).padStart(2, "0")}`,
    count,
  }));
}

function expandLegacyDeck(entries: readonly LegacyDeckEntry[]): readonly CardDefinitionId[] {
  return entries.flatMap((entry) =>
    Array.from({ length: entry.count }, () => legacyCardId(entry.cardId)),
  );
}

function legacyDeck(deckId: string, entries: readonly LegacyDeckEntry[]) {
  return { deckId, cardDefinitionIds: expandLegacyDeck(entries) } as const;
}

/** Exact pre-migration definition order used to create historical card-instance IDs and hashes. */
export const LEGACY_TEST_DECKS_V1 = {
  player1: legacyDeck("deck.test-player-1", [
    { cardId: "card.test-plains", count: 3 },
    { cardId: "card.test-mountain", count: 3 },
    { cardId: "card.test-river", count: 3 },
    ...legacyFillerEntries(1, 10, 3),
    { cardId: "card.test-filler-army-11", count: 1 },
  ]),
  player2: legacyDeck("deck.test-player-2", [
    { cardId: "card.test-river", count: 3 },
    { cardId: "card.test-plains", count: 3 },
    { cardId: "card.test-mountain", count: 3 },
    { cardId: "card.test-filler-army-04", count: 1 },
    ...legacyFillerEntries(5, 14, 3),
  ]),
  oneLand: legacyDeck("deck.test-one-land", [
    { cardId: "card.test-plains", count: 1 },
    ...legacyFillerEntries(1, 13, 3),
  ]),
  zeroLand: legacyDeck("deck.test-zero-land", [
    ...legacyFillerEntries(1, 13, 3),
    { cardId: "card.test-filler-army-14", count: 1 },
  ]),
  stageThree: legacyDeck("deck.test-stage-three", [
    { cardId: "card.test-plains", count: 3 },
    { cardId: "card.test-mountain", count: 1 },
    { cardId: "card.test-river", count: 1 },
    ...legacyFillerEntries(1, 11, 3),
    { cardId: "card.test-filler-army-12", count: 2 },
  ]),
  recycle: legacyDeck("deck.test-recycle", [
    { cardId: "card.test-mountain", count: 3 },
    { cardId: "card.test-river", count: 3 },
    { cardId: "card.test-plains", count: 3 },
    ...legacyFillerEntries(1, 10, 3),
    { cardId: "card.test-filler-army-14", count: 1 },
  ]),
} as const;
